import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const dismissKey = (pid, sid) => `script_health_dismissed_${pid}_${sid}`;

/**
 * Defensive banner: surfaces the "active script empty + archived sibling has
 * scenes" condition with a one-click restore CTA. Renders nothing in the
 * healthy case. Dismissible per-active-script via localStorage.
 *
 * Props:
 *   projectId    — required
 *   onRestored   — optional callback called after a successful restore
 */
export const ScriptHealthBanner = ({ projectId, onRestored }) => {
  const [health, setHealth] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const fetchHealth = async () => {
    if (!projectId) return;
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(
        `${API}/projects/${projectId}/script-health`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      setHealth(data);
      if (data?.active_script_id) {
        setDismissed(
          localStorage.getItem(dismissKey(projectId, data.active_script_id)) === '1',
        );
      }
    } catch {
      setHealth(null);
    }
  };

  useEffect(() => {
    fetchHealth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const shouldShow =
    !dismissed &&
    health?.has_healthier_archived_sibling === true &&
    health?.active_scene_count === 0;

  if (!shouldShow) return null;

  const handleRestore = async () => {
    setRestoring(true);
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      await axios.patch(
        `${API}/projects/${projectId}/scripts/${health.active_script_id}`,
        { is_archived: true },
        { headers },
      );
      await axios.patch(
        `${API}/projects/${projectId}/scripts/${health.healthier_archived_script_id}`,
        { is_archived: false },
        { headers },
      );
      toast.success('Working script restored. Reload to see updated scenes.');
      if (onRestored) onRestored();
      await fetchHealth();
    } catch (e) {
      toast.error('Restore failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setRestoring(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(dismissKey(projectId, health.active_script_id), '1');
    setDismissed(true);
  };

  return (
    <div
      data-testid="script-health-banner"
      className="flex items-start gap-3 p-4 mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-100"
    >
      <AlertTriangle className="flex-shrink-0 mt-0.5" size={20} />
      <div className="flex-1 text-sm">
        This project's active script has no scenes, but an archived script with{' '}
        <strong>{health.healthier_archived_scene_count} scenes</strong> exists.
      </div>
      <button
        data-testid="script-health-banner-restore"
        onClick={handleRestore}
        disabled={restoring}
        className="px-3 py-1.5 text-sm font-medium rounded bg-amber-500 text-zinc-900 hover:bg-amber-400 disabled:opacity-50"
      >
        {restoring ? 'Restoring…' : 'Restore working script'}
      </button>
      <button
        data-testid="script-health-banner-dismiss"
        onClick={handleDismiss}
        className="p-1 rounded hover:bg-amber-500/20"
        aria-label="Dismiss"
      >
        <X size={16} />
      </button>
    </div>
  );
};

export default ScriptHealthBanner;
